import { useState, useEffect, useCallback, useRef } from 'react';

interface StorageData<T> {
  formData: T;
  timestamp: string;
  version: string;
}

const EXPIRATION_TIME = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const DEBOUNCE_DELAY = 300; // 300ms debounce

export function useFormPersistence<T extends Record<string, any>>(
  storageKey: string,
  initialValues: T,
  version: string = '1.0'
) {
  const [values, setValues] = useState<T>(initialValues);
  const [isRestored, setIsRestored] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // Check if localStorage is available
  const isLocalStorageAvailable = useCallback(() => {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  // Load saved data on mount
  useEffect(() => {
    if (!isLocalStorageAvailable()) {
      console.warn('localStorage is not available');
      return;
    }

    try {
      const savedData = localStorage.getItem(storageKey);
      if (savedData) {
        const parsed: StorageData<T> = JSON.parse(savedData);

        // Check if data is expired
        const savedTime = new Date(parsed.timestamp).getTime();
        const now = Date.now();

        if (now - savedTime > EXPIRATION_TIME) {
          // Data expired, clear it
          localStorage.removeItem(storageKey);
          console.log(`Cleared expired form data for ${storageKey}`);
          return;
        }

        // Check version compatibility
        if (parsed.version !== version) {
          console.log(`Form version mismatch for ${storageKey}, clearing old data`);
          localStorage.removeItem(storageKey);
          return;
        }

        // Restore the form data
        setValues(parsed.formData);
        setIsRestored(true);
        console.log(`Restored form data for ${storageKey}`);
      }
    } catch (error) {
      console.error(`Error loading saved form data for ${storageKey}:`, error);
      // Clear corrupted data
      try {
        localStorage.removeItem(storageKey);
      } catch (e) {
        // Ignore cleanup errors
      }
    }
  }, [storageKey, version, isLocalStorageAvailable]);

  // Save to localStorage with debouncing
  const saveToStorage = useCallback(
    (data: T) => {
      if (!isLocalStorageAvailable()) {
        return;
      }

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new timer
      debounceTimerRef.current = setTimeout(() => {
        try {
          const storageData: StorageData<T> = {
            formData: data,
            timestamp: new Date().toISOString(),
            version,
          };
          localStorage.setItem(storageKey, JSON.stringify(storageData));
          console.log(`Saved form data for ${storageKey}`);
        } catch (error) {
          if (error instanceof Error && error.name === 'QuotaExceededError') {
            console.error('localStorage quota exceeded');
          } else {
            console.error(`Error saving form data for ${storageKey}:`, error);
          }
        }
      }, DEBOUNCE_DELAY);
    },
    [storageKey, version, isLocalStorageAvailable]
  );

  // Update single value
  const updateValue = useCallback(
    <K extends keyof T>(key: K, value: T[K]) => {
      setValues((prev) => {
        const newValues = { ...prev, [key]: value };
        saveToStorage(newValues);
        return newValues;
      });
    },
    [saveToStorage]
  );

  // Update multiple values
  const updateValues = useCallback(
    (updates: Partial<T>) => {
      setValues((prev) => {
        const newValues = { ...prev, ...updates };
        saveToStorage(newValues);
        return newValues;
      });
    },
    [saveToStorage]
  );

  // Clear storage
  const clearStorage = useCallback(() => {
    if (!isLocalStorageAvailable()) {
      return;
    }

    try {
      localStorage.removeItem(storageKey);
      console.log(`Cleared form data for ${storageKey}`);
      setIsRestored(false);
    } catch (error) {
      console.error(`Error clearing form data for ${storageKey}:`, error);
    }
  }, [storageKey, isLocalStorageAvailable]);

  // Check if storage has data
  const hasStoredData = useCallback(() => {
    if (!isLocalStorageAvailable()) {
      return false;
    }

    try {
      const savedData = localStorage.getItem(storageKey);
      return savedData !== null;
    } catch (error) {
      return false;
    }
  }, [storageKey, isLocalStorageAvailable]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    values,
    updateValue,
    updateValues,
    clearStorage,
    isRestored,
    hasStoredData,
  };
}
